/**
 * EligibilityAffidavit Component
 *
 * Renders the Sworn Declaration of Eligibility as a readable document
 * and collects the contestant's acknowledgement. Signing this affidavit
 * replaces the upload requirement for marriage certificate, divorce
 * decree, and death certificate documents.
 *
 * Two layout modes:
 *  - `layout="page"`  — full onboarding step layout (used in ApplicationFlow
 *    after the Rules & Regulations step, before profile creation).
 *  - `layout="card"`  — compact card layout for the ApplicantProfilePage,
 *    where a contestant who did not sign during onboarding (e.g. returning
 *    contestant) can sign later.
 *
 * When already signed, the checkbox is replaced with a read-only "Signed by
 * NAME on DATE" banner.
 */

import { useState } from 'react';
import { Check, ShieldAlert } from 'lucide-react';
import {
  AFFIDAVIT_TITLE,
  AFFIDAVIT_INTRO,
  AFFIDAVIT_SECTIONS,
  AFFIDAVIT_VERSION,
} from '@/data/affidavit';

export interface AffidavitSignature {
  signed: boolean;
  signedAt?: string;
  signedName?: string;
  version?: string;
}

export interface EligibilityAffidavitProps {
  /** Layout variant — see component docstring */
  layout: 'page' | 'card';
  /** Full legal name used as the typed signature (e.g. `user.fullName`) */
  signerName: string;
  /** Current signature state (from phase1Data) */
  signature?: AffidavitSignature;
  /** Called when the contestant submits their acknowledgement */
  onSign?: (payload: {
    signedAt: string;
    signedName: string;
    version: string;
  }) => void | Promise<void>;
  /** Disables the checkbox/button — used when saving or profile is locked */
  disabled?: boolean;
  /** Optional callback rendered as a back/skip button in page layout */
  onBack?: () => void;
  /** Error text to show inline under the checkbox */
  errorText?: string | null;
}

function formatSignedDate(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function EligibilityAffidavit({
  layout,
  signerName,
  signature,
  onSign,
  disabled = false,
  onBack,
  errorText,
}: EligibilityAffidavitProps): React.ReactElement {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const alreadySigned = !!signature?.signed;
  const signedName = signature?.signedName ?? signerName;
  const signedAt = signature?.signedAt;

  const canSubmit =
    !alreadySigned && !disabled && accepted && signerName.trim().length > 0;

  const shownError = errorText ?? localError;

  const handleSubmit = async () => {
    if (!canSubmit || !onSign) return;
    if (!signerName.trim()) {
      setLocalError(
        'We could not find your full legal name on your account. Update your profile name before signing.'
      );
      return;
    }
    setLocalError(null);
    setSubmitting(true);
    try {
      await onSign({
        signedAt: new Date().toISOString(),
        signedName: signerName.trim(),
        version: AFFIDAVIT_VERSION,
      });
    } catch (err) {
      console.error('Failed to record affidavit signature:', err);
      setLocalError('We could not record your signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const wrapperClass =
    layout === 'page'
      ? ''
      : 'bg-card border border-gold-200 rounded-lg p-6 lg:p-8 shadow-subtle';

  return (
    <div className={wrapperClass} data-testid="eligibility-affidavit">
      {layout === 'page' && onBack && (
        <button
          onClick={onBack}
          type="button"
          className="bg-transparent border-none text-gold-600 cursor-pointer mb-5 text-base font-sans text-xs uppercase tracking-wider hover:opacity-80 transition-all duration-300 ease-out touch-target"
        >
          ← Back to Rules & Agreement
        </button>
      )}

      <div className="flex items-start gap-3 mb-4">
        <ShieldAlert className="w-6 h-6 text-gold-950 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-serif text-2xl font-bold text-gold-950 mb-1">
            {AFFIDAVIT_TITLE}
          </h2>
        </div>
      </div>

      <p className="text-gold-700 mb-6 font-body text-sm leading-relaxed">
        {AFFIDAVIT_INTRO}
      </p>

      <div
        className="bg-gold-50 p-6 lg:p-8 mb-8 max-h-[55vh] overflow-y-auto border border-gold-200 rounded"
        data-testid="affidavit-document"
      >
        <div className="mb-6">
          <p className="font-body text-sm text-gold-950 leading-relaxed">
            <span className="text-eyebrow-sm text-gold-600 block mb-2">
              Contestant Affirmation
            </span>
            I,{' '}
            <span
              className="font-serif italic text-gold-950 border-b border-gold-400 px-2"
              data-testid="affidavit-typed-name"
            >
              {signerName || '—'}
            </span>{' '}
            ("Contestant"), do hereby affirm, acknowledge, and certify the
            following:
          </p>
        </div>

        {AFFIDAVIT_SECTIONS.map((section) => (
          <section key={section.heading} className="mb-6">
            <h3 className="font-serif text-lg font-bold text-gold-950 mb-2">
              {section.heading}
            </h3>
            {section.body && (
              <p className="font-body text-sm text-gold-950 leading-relaxed mb-3">
                {section.body}
              </p>
            )}
            {section.bullets && (
              <ul className="list-disc list-outside pl-5 space-y-1.5 font-body text-sm text-gold-950 leading-relaxed mb-3">
                {section.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
            {section.subsections?.map((sub) => (
              <div
                key={sub.heading}
                className="mt-3 pl-4 border-l-2 border-gold-200"
              >
                <h4 className="font-sans text-[11px] uppercase tracking-wider font-bold text-gold-950 mb-1.5">
                  {sub.heading}
                </h4>
                <p className="font-body text-sm text-gold-950 leading-relaxed">
                  {sub.body}
                </p>
              </div>
            ))}
          </section>
        ))}

        <p className="font-sans text-[9px] uppercase tracking-widest text-gold-500 pt-4 border-t border-gold-200">
          Declaration version {AFFIDAVIT_VERSION}
        </p>
      </div>

      {alreadySigned ? (
        <div
          className="bg-gold-100 border border-gold-300 rounded p-5 flex items-start gap-3"
          data-testid="affidavit-signed-banner"
        >
          <Check className="w-5 h-5 text-gold-950 shrink-0 mt-0.5" />
          <div>
            <p className="font-sans text-xs font-bold uppercase tracking-widest text-gold-950">
              Declaration Signed
            </p>
            <p className="font-body text-sm text-gold-800 mt-1">
              Signed by{' '}
              <span className="font-serif italic">{signedName}</span>
              {signedAt ? ` on ${formatSignedDate(signedAt)}` : ''}.
            </p>
            {signature?.version && signature.version !== AFFIDAVIT_VERSION && (
              <p className="font-sans text-[10px] uppercase tracking-wider text-gold-600 mt-2">
                Signed an earlier version ({signature.version}). Current
                version: {AFFIDAVIT_VERSION}.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="affidavit-acceptance"
              checked={accepted}
              disabled={disabled || submitting}
              onChange={(e) => {
                setAccepted(e.target.checked);
                if (e.target.checked) setLocalError(null);
              }}
              className="mt-0.5 h-4 w-4 rounded bg-gold-200 border-none text-gold-950 focus:ring-gold-500/30 disabled:opacity-50"
              data-testid="affidavit-acceptance-checkbox"
              aria-required="true"
              aria-invalid={!!shownError}
            />
            <label
              htmlFor="affidavit-acceptance"
              className="font-sans text-xs font-bold uppercase tracking-widest text-gold-700 leading-relaxed"
            >
              I, {signerName || '[your name]'}, have read the declaration above
              and I affirm under penalty of perjury under the laws of the State
              of Idaho that all statements are true, complete, and correct.
            </label>
          </div>

          {shownError && (
            <div
              className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm"
              role="alert"
              data-testid="affidavit-error"
            >
              {shownError}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="px-8 py-3 min-h-[var(--touch-target-min)] bg-gold-950 text-gold-50 text-eyebrow-sm font-bold transition-all duration-300 hover:shadow-raised disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="affidavit-sign-button"
          >
            {submitting ? 'Recording Signature…' : 'Sign Declaration'}
          </button>
        </div>
      )}
    </div>
  );
}

export default EligibilityAffidavit;
